/**
 * /upload/:projectId/:env?ref=… — 「ゲームのファイルを入れるだけの小さな画面」（2026-10-04 creator/web-upload）。
 *
 * WHY: founder「Claude と ChatGPT のプラグインにしたい。そうすると Web の Claude とか、Web の ChatGPT からでもゲームがアップロードできるように
 *   なるでしょ」。Web のチャットには手元のファイルが無く、AI が道具に渡せるのは小さなゲームまで。AI（MCP の hosting_upload_page）が
 *   このページをボタン（URL モードの elicitation）かリンクで出し、本人はチャットで作られた zip（かフォルダ）を落として「入れる」を押すだけ。
 *   MCP は本体の版の一覧で入り終わりを見つけ、チャットに「公開しました」（公開中のアプリなら確認）を返す（apps/mcp/src/tools/uploadPage.ts）。
 *   判断と上げ方は ../uploadFiles.ts（+ test）・zip を開くのは ../zipRead.ts（外の部品なし・ブラウザの DecompressionStream）。
 *
 * ログインしていなければ App.tsx のログイン前の木が /login へ送り、ログイン後に `state.from` でここへ戻る（EnterKeyPage と同じ）。
 * 守り: 上げるのはログインしたセッション（本体がプロジェクトの持ち主かを確かめる）。公開中のアプリは ready まで = 切り替えは MCP の確認。
 *   ファイルの名前（zip の中の名前）は React がテキストとして描く。何も送らないうちは何も変わらない（落とす / 選ぶ → 「入れる」で初めて送る）。
 */
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { api, ApiError, envPath, type ProjectDetail } from '../api';
import { liveUrlOf } from '../confirmPlain';
import { useResource } from '../hooks';
import { errorText, useT, type MessageKey } from '../i18n';
import { keyFoundFiles, normalizeUpload, readUpload, sizeText, uploadAll, uploadErrorKey, type PickedFile, type UploadApi, type UploadTarget } from '../uploadFiles';
import { readZip, ZipError } from '../zipRead';
import { AuthShell } from './AccountRecovery';

export function UploadPage() {
  const { t } = useT();
  const params = useParams();
  const location = useLocation();
  const target = readUpload(params, location.search);
  if (!target) {
    return (
      <AuthShell title={t('upload.title')}>
        <p className="alert alert-error" role="alert">
          {t('upload.badLink')}
        </p>
      </AuthShell>
    );
  }
  return <UploadForm {...target} />;
}

/** 本体への通信（api.ts を包む）。raw = 生のバイトの PUT（blob） */
const uploadApi: UploadApi = {
  json: (path, init) => api(path, { method: init.method, body: init.body }),
  raw: async (path, bytes) => {
    await api(path, { method: 'PUT', raw: bytes });
  }
};

const sha256 = async (b: Uint8Array) =>
  [...new Uint8Array(await crypto.subtle.digest('SHA-256', b as BufferSource))].map((x) => x.toString(16).padStart(2, '0')).join('');

type Picked = { files: PickedFile[]; skipped: number; totalBytes: number };

function UploadForm(target: UploadTarget) {
  const { t } = useT();
  const { projectId, env } = target;
  // プロジェクトの名前（見出し）とアプリの本物の URL（どこに入るか）。読めなくても入れられる（本体が持ち主を確かめる）
  const project = useResource<{ project: ProjectDetail }>(`/v1/projects/${projectId}`);
  const hosting = useResource<{ url?: unknown }>(`${envPath(projectId, env)}/hosting`);
  const appUrl = liveUrlOf(hosting.data?.url);
  const name = project.data?.project.name;
  const [picked, setPicked] = useState<Picked | null>(null);
  const [busy, setBusy] = useState<'reading' | 'sending' | null>(null);
  const [percent, setPercent] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ url: string | null } | null>(null);
  const [over, setOver] = useState(false);
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  /** 集めたファイル → 整える（上のフォルダを外す・ごみを外す・index.html が要る） */
  const take = (files: PickedFile[]) => {
    const r = normalizeUpload(files);
    if (!r.ok) {
      setPicked(null);
      setErr(t(r.issue === 'empty' ? 'upload.issue.empty' : 'upload.issue.noIndex'));
      return;
    }
    setPicked({ files: r.files, skipped: r.skipped, totalBytes: r.totalBytes });
  };

  /** zip 1 つ → 開く。開けなければ理由（zipRead の code）を本人の言葉で */
  const takeZip = async (file: File) => {
    if (typeof DecompressionStream === 'undefined') return setErr(t('upload.noUnzip'));
    setBusy('reading');
    try {
      take(await readZip(new Uint8Array(await file.arrayBuffer())));
    } catch (e) {
      setErr(e instanceof ZipError ? t(`upload.zip.${e.code}` as MessageKey) : t('upload.zip.not_zip'));
    } finally {
      setBusy(null);
    }
  };

  /** 選んだ / 落としたファイルの並び（フォルダを選ぶと webkitRelativePath = "my-game/index.html"） */
  const takeList = async (list: File[]) => {
    setErr(null);
    setDone(null);
    if (list.length === 1 && /\.zip$/i.test(list[0]!.name)) return takeZip(list[0]!);
    setBusy('reading');
    try {
      take(await Promise.all(list.map(async (f) => ({ path: f.webkitRelativePath || f.name, bytes: new Uint8Array(await f.arrayBuffer()) }))));
    } finally {
      setBusy(null);
    }
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (busy) return;
    // フォルダを落としたとき: DataTransferItem の webkitGetAsEntry でたどる（files には中身が来ない）
    const entries = [...e.dataTransfer.items].map((i) => i.webkitGetAsEntry?.()).filter((x): x is FileSystemEntry => !!x);
    if (entries.some((x) => x.isDirectory)) {
      setErr(null);
      setBusy('reading');
      try {
        const files: PickedFile[] = [];
        for (const entry of entries) await walk(entry, '', files);
        take(files);
      } finally {
        setBusy(null);
      }
      return;
    }
    await takeList([...e.dataTransfer.files]);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const list = [...(e.target.files ?? [])];
    e.target.value = '';
    if (list.length) void takeList(list);
  };

  const send = async () => {
    if (!picked || busy) return;
    setBusy('sending');
    setErr(null);
    setPercent(0);
    try {
      const r = await uploadAll({ api: uploadApi, sha256, onProgress: (d, total) => setPercent(total ? Math.floor((d / total) * 100) : 100) }, target, picked.files);
      setDone({ url: env === 'development' ? r.url : null });
      setPicked(null);
    } catch (e) {
      if (e instanceof ApiError) {
        const key = uploadErrorKey(e.code);
        if (e.code === 'secret_like_content') setErr(t('upload.error.keyFound', { files: keyFoundFiles(e.details).join(', ') || '—' }));
        else setErr(errorText(e, key ? { [e.code]: key as MessageKey } : undefined));
      } else setErr(String(e));
    } finally {
      setBusy(null);
    }
  };

  const title = name ? t('upload.titleFor', { name }) : t('upload.title');

  if (done) {
    return (
      <AuthShell title={t('upload.doneTitle')}>
        <p className="alert alert-ok" role="status">
          {t(`upload.doneBody.${env}`)}
        </p>
        {done.url && <p className="upload-url">{t('upload.doneUrl', { url: done.url })}</p>}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDone(null)}>
          {t('upload.again')}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={title}>
      <p className="muted">{t('upload.lead')}</p>
      {/* どこに入るか（公開中のアプリは目立たせる）。URL はテキストとして描く */}
      <p className={env === 'production' ? 'alert alert-info' : 'small'}>{appUrl ? t(`upload.where.${env}`, { url: appUrl }) : t(`upload.where.${env}NoUrl`)}</p>
      <div
        className={`upload-drop${over ? ' upload-drop-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => void onDrop(e)}
      >
        <strong>{t('upload.drop')}</strong>
        <span className="small muted">{t('upload.or')}</span>
        <div className="upload-pick">
          <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => zipInput.current?.click()}>
            {t('upload.pickZip')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => folderInput.current?.click()}>
            {t('upload.pickFolder')}
          </button>
        </div>
        <input ref={zipInput} type="file" accept=".zip,application/zip" hidden onChange={onPick} />
        {/* フォルダを選ぶ（webkitdirectory は型に無い = 属性として渡す。Chrome・Safari・Firefox とも対応） */}
        <input ref={folderInput} type="file" hidden onChange={onPick} {...({ webkitdirectory: '', directory: '' } as Record<string, string>)} />
      </div>
      {busy === 'reading' && <p className="small muted">{t('upload.reading')}</p>}
      {picked && (
        <>
          <p className="small">{t('upload.ready', { count: String(picked.files.length), size: sizeText(picked.totalBytes) })}</p>
          {picked.skipped > 0 && <p className="small muted">{t('upload.skipped', { count: String(picked.skipped) })}</p>}
        </>
      )}
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-primary" disabled={!picked || !!busy} onClick={() => void send()}>
          {busy === 'sending' ? t('upload.working', { percent: String(percent) }) : t('upload.button')}
        </button>
      </div>
    </AuthShell>
  );
}

/** 落としたフォルダをたどる（FileSystemEntry）。readEntries は 1 回に最大 100 件ずつ返す = 空になるまで呼ぶ */
async function walk(entry: FileSystemEntry, prefix: string, out: PickedFile[]): Promise<void> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
    out.push({ path, bytes: new Uint8Array(await file.arrayBuffer()) });
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
    if (!batch.length) break;
    for (const child of batch) await walk(child, path, out);
  }
}
